/* D81 — Sub-dialogs and preview overlay for the Client Media Library.
 * Split out from ClientMediaLibrary.jsx to keep both files small enough
 * for the visual-edits babel plugin to traverse without blowing the
 * Node call stack.
 *
 * D81b additions:
 *   - <AuthImg>: authenticated <img> tag — fetches the resource with
 *     the Bearer token, converts to a blob URL, then renders. Used by
 *     the FileCard thumbnails which can't send Authorization headers
 *     via plain `<img src>`.
 *   - <ShareDialog>: now a Drive-style dialog supporting both FILES and
 *     FOLDERS with a Restricted ↔ Anyone-with-link toggle, Copy, and
 *     Revoke.
 */
import { useEffect, useState, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Folder, FolderOpen, File as FileIcon, Copy, Lock, Globe2, Check,
  Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { clientMediaAPI } from '../lib/api';
import { formatDate } from '../lib/dateFormat';

function humanSize(bytes) {
  if (!bytes) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0; let v = bytes;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function isPreviewable(ct) {
  return !!ct && (
    ct.startsWith('image/') || ct.startsWith('video/') || ct === 'application/pdf'
  );
}

// D81b — Rewrite an absolute media URL so it ALWAYS routes through the
// frontend's configured backend host. The backend may embed URLs that
// point at a stale Kubernetes ingress (e.g. an old pod hostname stored
// in supervisor's APP_URL env) which 404s from the browser; we don't
// want to be at the mercy of that. By extracting just the path + query
// and re-prefixing with REACT_APP_BACKEND_URL we get a URL we KNOW the
// browser can reach.
function _rewriteToBackend(url) {
  if (!url) return url;
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  if (!base) return url;
  try {
    const u = new URL(url);
    return `${base}${u.pathname}${u.search}`;
  } catch {
    // Already a relative path → just prefix it.
    return url.startsWith('/') ? `${base}${url}` : url;
  }
}

// D81b — Authenticated <img>. <img src=...> can't send Authorization
// headers, so thumbnails served behind the /api/client-media/.../thumb
// endpoint render as broken images. This component fetches the URL with
// the Bearer token, converts the response to a blob URL, then renders.
//
// Props:
//   src       — URL to fetch (e.g. file.thumb_url)
//   alt       — alt text
//   className — passed through to the <img>
//   fallback  — element to render while loading OR when fetch fails
//
// Behaviour:
//   - On unmount or when `src` changes, the previous blob URL is revoked
//     to avoid memory leaks.
//   - When src is empty, renders the fallback immediately.
//   - When fetch fails (404, 401, network), keeps rendering the fallback
//     — never a broken-image icon.
export function AuthImg({ src, alt = '', className = '', fallback = null, ...rest }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
    if (!src) { setBlobUrl(null); return; }
    let cancelled = false;
    let created = null;
    const token = localStorage.getItem('stellar_token');
    (async () => {
      try {
        const r = await fetch(_rewriteToBackend(src), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'omit',
        });
        if (!r.ok) {
          if (!cancelled) setErrored(true);
          return;
        }
        const b = await r.blob();
        if (cancelled) return;
        created = URL.createObjectURL(b);
        setBlobUrl(created);
      } catch {
        if (!cancelled) setErrored(true);
      }
    })();
    return () => {
      cancelled = true;
      if (created) URL.revokeObjectURL(created);
    };
  }, [src]);
  if (!src || errored || !blobUrl) return fallback;
  return <img src={blobUrl} alt={alt} className={className} {...rest} />;
}

export function NewFolderDialog({ open, parent_id, folders, onClose, onCreate }) {
  const [name, setName] = useState('');
  useEffect(() => { if (open) setName(''); }, [open]);
  const parent = folders.find(f => f.id === parent_id);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>{parent ? `Inside "${parent.name}"` : 'In root'}</DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Folder name"
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onCreate(name.trim(), parent_id); }}
          data-testid="d81-new-folder-name"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => name.trim() && onCreate(name.trim(), parent_id)} disabled={!name.trim()} data-testid="d81-new-folder-create">
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RenameDialog({ open, target, kind, onClose, onSubmit }) {
  const [name, setName] = useState('');
  useEffect(() => { if (open && target) setName(target.name || target.filename || ''); }, [open, target]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename {kind === 'folder' ? 'folder' : 'file'}</DialogTitle>
        </DialogHeader>
        <Input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onSubmit(name.trim()); }}
          data-testid="d81-rename-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => name.trim() && onSubmit(name.trim())} disabled={!name.trim()} data-testid="d81-rename-submit">
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MoveDialog({ open, file, folders, onClose, onSubmit }) {
  const [targetId, setTargetId] = useState('');
  useEffect(() => { if (open) setTargetId(''); }, [open]);
  const labels = useMemo(() => {
    const idx = {}; for (const f of folders) idx[f.id] = f;
    const pathFor = (f) => {
      const out = [f.name]; let cur = f;
      while (cur.parent_id) { cur = idx[cur.parent_id]; if (!cur) break; out.unshift(cur.name); }
      return out.join(' / ');
    };
    return folders.map(f => ({ id: f.id, label: pathFor(f) }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' }));
  }, [folders]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move "{file?.filename}"</DialogTitle>
          <DialogDescription>Choose a destination folder.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-0.5" data-testid="d81-move-list">
          <button
            onClick={() => setTargetId('__root__')}
            className={`w-full text-left px-3 py-2 rounded-md text-[13px] ${targetId === '__root__' ? 'bg-gold/15 text-gold' : 'hover:bg-foreground/[0.04]'}`}
            data-testid="d81-move-root"
          >
            <FolderOpen className="h-3.5 w-3.5 inline mr-2" /> All Media (root)
          </button>
          {labels.map(l => (
            <button
              key={l.id}
              onClick={() => setTargetId(l.id)}
              className={`w-full text-left px-3 py-2 rounded-md text-[13px] ${targetId === l.id ? 'bg-gold/15 text-gold' : 'hover:bg-foreground/[0.04]'}`}
              data-testid={`d81-move-target-${l.id}`}
            >
              <Folder className="h-3.5 w-3.5 inline mr-2" /> {l.label}
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSubmit(targetId === '__root__' ? null : targetId)}
            disabled={!targetId}
            data-testid="d81-move-submit"
          >
            Move here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteConfirm({ open, target, kind, onClose, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Delete {kind === 'folder' ? 'folder' : 'file'}?</DialogTitle>
          <DialogDescription>
            {kind === 'folder'
              ? `"${target?.name}" and ALL files inside (and any subfolders) will be removed.`
              : `"${target?.filename}" will be removed. This can't be undone.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={onConfirm}
            className="bg-red-500/90 hover:bg-red-500 text-white"
            data-testid="d81-delete-confirm"
          >
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// D81b — Drive-style share dialog. Used for BOTH files and folders.
//
// Props:
//   open       — boolean
//   kind       — 'file' | 'folder'
//   target     — the file or folder doc (carries `share_token`, `share_access`, `share_url`/`public_url`)
//   share      — the latest share result from a fresh shareFile/shareFolder call (overrides target.share_token if present)
//   onClose    — close handler
//   onSetAccess(access)  — async fn(access: 'restricted'|'anyone') → must
//                          return the new share record (with share_url + share_access)
//   onRevoke()           — async fn → revokes the share entirely (token + access)
//
// Behaviour:
//   - First open: defaults to 'restricted'.
//   - Toggle between 'restricted' and 'anyone' triggers onSetAccess(mode);
//     the token DOES NOT rotate, so the URL stays the same.
//   - Copy uses navigator.clipboard with a textarea fallback.
//   - Revoke clears the token; the dialog closes immediately.
export function ShareDialog({ open, kind = 'file', target, share, onClose, onSetAccess, onRevoke }) {
  const currentShare = share || (target?.share_token ? {
    share_token: target.share_token,
    share_url: target.public_url || target.share_url,
    share_access: target.share_access || 'restricted',
  } : null);
  const url = currentShare?.share_url || '';
  const access = currentShare?.share_access || 'restricted';
  const [busy, setBusy] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      // Fallback: textarea + execCommand for legacy browsers / blocked clipboard.
      try {
        const ta = document.createElement('textarea');
        ta.value = url; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        toast.success('Link copied');
      } catch {
        toast.error('Copy failed — select the link manually.');
      }
    }
  };

  const setAccess = async (next) => {
    if (busy || !onSetAccess) return;
    if (next === access) return;
    setBusy(true);
    try { await onSetAccess(next); } finally { setBusy(false); }
  };

  const revoke = async () => {
    if (busy || !onRevoke) return;
    setBusy(true);
    try { await onRevoke(); onClose(); } finally { setBusy(false); }
  };

  const targetLabel = kind === 'folder' ? `folder "${target?.name || ''}"` : `"${target?.filename || ''}"`;
  const restrictedDesc = kind === 'folder'
    ? 'Only people you grant access to via Stellar can open this folder.'
    : 'Only people you grant access to via Stellar can open this file.';
  const anyoneDesc = kind === 'folder'
    ? 'Anyone with the link can open this folder and everything inside it (including files added later).'
    : 'Anyone with the link can open this file.';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="d81-share-dialog">
        <DialogHeader>
          <DialogTitle>Share {targetLabel}</DialogTitle>
          <DialogDescription>
            Link-only sharing. No email will be sent.
          </DialogDescription>
        </DialogHeader>

        {/* General access selector — Drive style. */}
        <div className="space-y-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/60">General access</p>
          <div className="rounded-lg border border-border/30 divide-y divide-border/20 overflow-hidden">
            <button
              onClick={() => setAccess('restricted')}
              disabled={busy}
              className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${
                access === 'restricted' ? 'bg-gold/10' : 'hover:bg-foreground/[0.03]'
              }`}
              data-testid="d81-share-access-restricted"
            >
              <Lock className={`h-4 w-4 mt-0.5 shrink-0 ${access === 'restricted' ? 'text-gold' : 'text-muted-foreground/60'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-foreground">Restricted</p>
                <p className="text-[11px] text-muted-foreground/60">{restrictedDesc}</p>
              </div>
              {access === 'restricted' && <Check className="h-4 w-4 text-gold shrink-0" />}
            </button>
            <button
              onClick={() => setAccess('anyone')}
              disabled={busy}
              className={`w-full text-left px-3 py-2.5 flex items-start gap-3 transition-colors ${
                access === 'anyone' ? 'bg-gold/10' : 'hover:bg-foreground/[0.03]'
              }`}
              data-testid="d81-share-access-anyone"
            >
              <Globe2 className={`h-4 w-4 mt-0.5 shrink-0 ${access === 'anyone' ? 'text-gold' : 'text-muted-foreground/60'}`} />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-foreground">Anyone with the link</p>
                <p className="text-[11px] text-muted-foreground/60">{anyoneDesc}</p>
              </div>
              {access === 'anyone' && <Check className="h-4 w-4 text-gold shrink-0" />}
            </button>
          </div>
        </div>

        {/* Link row. */}
        <div className="space-y-1.5">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground/60">Link</p>
          <div className="flex gap-2 items-center">
            <Input value={url} readOnly className="text-[12px] font-mono" data-testid="d81-share-url" />
            <Button size="sm" onClick={copy} disabled={!url || busy} data-testid="d81-share-copy">
              <Copy className="h-3.5 w-3.5 mr-1" /> Copy
            </Button>
          </div>
          {access === 'restricted' && (
            <p className="text-[11px] text-amber-300/70 flex items-center gap-1.5" data-testid="d81-share-restricted-hint">
              <Lock className="h-3 w-3" /> Recipient must sign in to Stellar to open this link.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={revoke}
            disabled={busy || !url}
            className="text-red-400"
            data-testid="d81-share-revoke"
          >
            Revoke link
          </Button>
          <Button onClick={onClose} disabled={busy}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PreviewOverlay({ file, onClose }) {
  const token = localStorage.getItem('stellar_token');
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    if (!file) { setBlobUrl(null); return; }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const r = await fetch(_rewriteToBackend(file.download_url), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'omit',
        });
        if (!r.ok) return;
        const b = await r.blob();
        if (!cancelled) {
          createdUrl = URL.createObjectURL(b);
          setBlobUrl(createdUrl);
        }
      } catch {
        /* swallow — overlay shows the inline "Loading…" / no-preview state. */
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [file, token]);
  if (!file) return null;
  const ct = file.content_type || '';
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="truncate">{file.filename}</DialogTitle>
          <DialogDescription>{humanSize(file.file_size)} · {ct}</DialogDescription>
        </DialogHeader>
        <div className="bg-black/20 rounded-lg overflow-hidden max-h-[70vh] flex items-center justify-center" data-testid="d81-preview-overlay">
          {!isPreviewable(ct) ? (
            <div className="py-16 text-center">
              <FileIcon className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-[13px] text-muted-foreground">No inline preview available.</p>
            </div>
          ) : !blobUrl ? (
            <p className="py-16 text-muted-foreground/40 text-[13px]">Loading…</p>
          ) : ct.startsWith('image/') ? (
            <img src={blobUrl} alt={file.filename} className="max-h-[70vh] object-contain" />
          ) : ct.startsWith('video/') ? (
            <video src={blobUrl} controls className="max-h-[70vh] w-full" />
          ) : ct === 'application/pdf' ? (
            <iframe src={blobUrl} title={file.filename} className="w-full h-[70vh]" />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── D90 — Import approved task-submission files into the media library ──
// Lists every APPROVED submission file for this client's tasks; user
// multi-selects and the backend copies blobs server-side (no re-upload).
export function ImportFromSubmissionsDialog({ open, onClose, workspaceId, clientId, currentFolderId, onImported }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  useEffect(() => {
    if (!open) { setSelected(new Set()); return; }
    setLoading(true);
    clientMediaAPI.listApprovedSubmissionFiles(workspaceId, clientId)
      .then(r => setItems(r.data || []))
      .catch(() => toast.error('Failed to load approved submissions'))
      .finally(() => setLoading(false));
  }, [open, workspaceId, clientId]);

  const toggle = (fileId) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(fileId)) n.delete(fileId); else n.add(fileId);
      return n;
    });
  };

  const selectable = items.filter(i => !i.already_imported);

  const doImport = async () => {
    const payload = items
      .filter(i => selected.has(i.file_id))
      .map(i => ({ task_id: i.task_id, submission_id: i.submission_id, file_id: i.file_id }));
    if (!payload.length) return;
    setImporting(true);
    try {
      const r = await clientMediaAPI.importFromSubmissions(workspaceId, clientId, payload, currentFolderId || null);
      const ok = (r.data?.imported || []).length;
      const skip = (r.data?.skipped || []).length;
      if (ok) toast.success(`${ok} file${ok > 1 ? 's' : ''} added to Media${skip ? ` (${skip} skipped)` : ''}`);
      else toast.warning('Nothing imported' + (skip ? ` — ${r.data.skipped[0]?.reason || ''}` : ''));
      onImported?.(r.data?.imported || []);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="d90-import-dialog">
        <DialogHeader>
          <DialogTitle>Add from approved submissions</DialogTitle>
          <DialogDescription>
            Files approved on this client's tasks — select to copy them into the media library without re-uploading.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1">
          {loading ? (
            <p className="text-center text-[13px] text-muted-foreground/50 py-8">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-center text-[13px] text-muted-foreground/50 py-8" data-testid="d90-import-empty">
              No approved submission files found for this client's tasks yet.
            </p>
          ) : (
            <div className="space-y-1">
              {items.map(i => (
                <label
                  key={i.file_id}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg border border-border/20 ${i.already_imported ? 'opacity-50' : 'hover:bg-foreground/[0.03] cursor-pointer'}`}
                  data-testid={`d90-import-row-${i.file_id}`}
                >
                  <Checkbox
                    checked={selected.has(i.file_id)}
                    disabled={i.already_imported}
                    onCheckedChange={() => toggle(i.file_id)}
                    data-testid={`d90-import-check-${i.file_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-foreground truncate">{i.filename}</p>
                    <p className="text-[11px] text-muted-foreground/50 truncate">
                      {i.task_title} · V{i.version} · {humanSize(i.size_bytes)}
                      {i.approved_at ? ` · Approved ${formatDate(i.approved_at)}` : ''}
                    </p>
                  </div>
                  {i.already_imported && (
                    <span className="text-[10px] text-gold/80 shrink-0 inline-flex items-center gap-1">
                      <Check className="h-3 w-3" /> Added
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={importing}>Cancel</Button>
          <Button
            onClick={doImport}
            disabled={importing || selected.size === 0 || !selectable.length}
            className="bg-gold/10 hover:bg-gold/20 text-gold border-0"
            data-testid="d90-import-submit"
          >
            {importing ? 'Adding…' : `Add to Media${selected.size ? ` (${selected.size})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
